'use client';

import { FormattedMessage } from 'react-intl';
import Button from '../Button';
import { cartDatas } from '@/types/main';
import { ICurrency } from '@/types/currency';
import { getPrice } from '@/utils/getPrice';
import { usePickupTime } from '@/hooks/usePickupTime';
import { trackEvent } from '../Event/_lib/eventTrack';
import { Image } from '@/components/Image';
import PageViewEvent from '../Event/PageViewEvent';

interface CartModalProps {
  items: cartDatas[];
  onClose?: () => void;
  onAllItemDelete?: () => void;
  onItemDelete?: (item: cartDatas) => void;
  onSubmit?: (items: cartDatas[]) => void;
  currency: ICurrency;
  isSoloArtist: boolean;
  offerTotal?: number;
}

const CartModal = ({
  items,
  onClose,
  onAllItemDelete,
  onItemDelete,
  onSubmit,
  currency,
  isSoloArtist,
  offerTotal
}: CartModalProps) => {
  const { pickupDate, pickupDateTwo, pickupTime, pickupTimeTwo, isSamePickupTime, isBetweenTime } = usePickupTime();

  return (
    <PageViewEvent eventName="PLP_cart_view" eventProps={{ card_count: items.length, total: offerTotal }}>
      <div
        className="fixed inset-0 bg-black/85 flex justify-end z-10"
        onClick={() => {
          onClose?.();
        }}
      >
        <div className="w-1/2 absolute flex flex-col items-center gap-6 top-[300px] left-[90px] text-white">
          <Image src="/icons/bell.svg" alt="bell-svg" width={110} height={110} />
          <p className="text-[30px] leading-9 font-extrabold text-center">
            <FormattedMessage id="today_pick_up_schedule" defaultMessage="Today’s pick-up schedule" />
          </p>
          <div className="flex flex-col gap-6">
            <div style={{ marginTop: pickupTimeTwo && 24 }}>
              <div className="text-center flex flex-col items-center text-[20px] leading-7 font-medium">
                {isBetweenTime && !isSamePickupTime ? (
                  <>
                    <div className="w-max bg-[#ff6060] text-white text-[20px] font-semibold px-[16.5px] rounded mb-2">
                      <FormattedMessage id="pick_up_notice_if_one" defaultMessage={'If the order is completed by 4:00 PM'} />
                    </div>
                    <FormattedMessage
                      id="pick_up_notice_one"
                      defaultMessage={'If the order is completed by 4:00 PM'}
                      values={{ pickupDate, pickupHour: pickupTime }}
                    />
                    <>
                      <div className="w-max bg-[#ff6060] text-white text-[20px] font-semibold px-[10px] rounded mb-2 mt-8">
                        <FormattedMessage id="pick_up_notice_if_two" defaultMessage={'If the order is completed after 4:00 PM'} />
                      </div>
                      <FormattedMessage
                        id="pick_up_notice_if_two_desc"
                        defaultMessage={'Pick-up available from {pickupDateTwo} {pickupHour} PM'}
                        values={{ pickupDateTwo, pickupHour: pickupTimeTwo }}
                      />
                    </>
                  </>
                ) : (
                  <FormattedMessage
                    id="pick_up_notice_one"
                    defaultMessage={'If the order is completed by 4:00 PM'}
                    values={{ pickupDate, pickupHour: pickupTime }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="w-[571px] h-full bg-white flex flex-col"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="w-full p-[40px] pb-5">
            <div className="w-full flex justify-between mb-2">
              <span className="text-[30px] leading-9 font-extrabold">
                <FormattedMessage id="cart-modal_title" defaultMessage="My cart" />
              </span>
              <Image
                src="/icons/close.svg"
                alt="close icon"
                className="w-9 h-9 cursor-pointer"
                onClick={() => {
                  onClose?.();
                }}
                width={36}
                height={36}
              />
            </div>
            <span className="text-[18px] leading-7 font-medium text-[#707070]">
              <FormattedMessage id="cart-modal_description" defaultMessage="The maximum quantity available for 1 order is 40." />
            </span>
          </div>
          <div className="w-full py-[18px] px-[30px] flex justify-between items-center border-y border-[#f5f5f5]">
            <span className="text-[20px] leading-7 font-medium">
              <FormattedMessage id="cart-modal_quantity" defaultMessage="Quantity" />: {items.length}{' '}
              <FormattedMessage id="cart-modal_items" defaultMessage="items" />
            </span>
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => {
                onAllItemDelete?.();
                trackEvent('PLP_cart_click_Delete_button', { name: 'all' });
              }}
            >
              <Image src="/icons/delete.svg" alt="delete icon" className="w-6 h-6" width={24} height={24} />
              <span className="text-[16px] leading-6 font-semibold">
                <FormattedMessage id="delete_all" defaultMessage="Delete all" />
              </span>
            </div>
          </div>
          <div className="w-full flex-1 flex flex-col overflow-y-scroll">
            {items.map((item) => (
              <div
                key={item.offer.id}
                className="w-full py-6 px-[30px] h-[175px] border-b border-[#f5f5f5] flex items-center gap-4"
              >
                <Image
                  src={item.photo_card.image}
                  alt="photocard"
                  className="w-[80px] h-[124px] rounded-[12px] object-cover"
                  height={80}
                  width={124}
                />
                <div className="flex-1 flex flex-col justify-between h-full">
                  <div className="flex flex-col gap-1">
                    <span className="text-[16px] leading-6 font-bold">{`${item.photo_card.group.name_en} ${
                      isSoloArtist ? '' : item.photo_card.member.name_en
                    }`}</span>
                    <span className="text-[14px] leading-5 font-normal text-[#808080] w-full overflow-hidden text-ellipsis h-10">
                      {item.photo_card.name_en}
                    </span>
                  </div>
                  <span className="text-[20px] leading-7 font-bold">{getPrice(String(item.offer.price), currency)}</span>
                </div>
                <div
                  className="w-12 h-full flex justify-end items-center cursor-pointer"
                  onClick={() => {
                    onItemDelete?.(item);
                    trackEvent('PLP_cart_click_Delete_button', { name: 'single' });
                  }}
                >
                  <Image src="/icons/delete.svg" alt="delete icon" className="w-6 h-6" width={24} height={24} />
                </div>
              </div>
            ))}
          </div>
          <div className="w-full py-4 px-[30px] bg-[#f5f5f5] flex justify-between items-center">
            <span className="text-[20px] leading-7 font-bold">
              <FormattedMessage id="cart-modal_price" defaultMessage="Price" />:
            </span>
            <span className="text-[20px] leading-7 font-bold">
              {getPrice(items.reduce((prev, cur) => (prev += Number(cur.offer.price)), 0).toString(), currency)}
            </span>
          </div>
          <div className="pt-4 pb-5 px-[30px] w-full">
            <Button
              data-primary
              className="w-full h-[66px]"
              onClick={() => {
                trackEvent('PLP_cart_click_Order_button');
                onSubmit?.(items);
              }}
              disabled={items.length === 0 || items.length > 40}
            >
              <FormattedMessage id="cart-modal_proceed" defaultMessage="Place an order" />
            </Button>
          </div>
        </div>
      </div>
    </PageViewEvent>
  );
};

export default CartModal;
